import React, { useState } from 'react';
import { 
  Calendar,
  Clock, 
  Flame, 
  Activity, 
  ChevronDown, 
  ChevronUp, 
  AlertCircle 
} from 'lucide-react';

// Intensity level colors
const intensityColors = {
  low: "bg-green-100 text-green-800",
  moderate: "bg-blue-100 text-blue-800",
  high: "bg-orange-100 text-orange-800",
  rest: "bg-gray-100 text-gray-800",
  inactive: "bg-red-100 text-red-800"
};

const intensityIcons = {
  low: <Activity size={14} className="text-green-800" />,
  moderate: <Activity size={14} className="text-blue-800" />,
  high: <Activity size={14} className="text-orange-800" />,
  rest: <Clock size={14} className="text-gray-800" />,
  inactive: <AlertCircle size={14} className="text-red-800" />
};

// Helper function to safely capitalize first letter
const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const ActivityChart = ({ data, activityLevels }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("steps");
  const [showTooltip, setShowTooltip] = useState(null);
  
  // Use provided data or fallback to example data
  const chartData = data || [
    { day: 'Mon', steps: 9123, intensity: 'moderate', activeMinutes: 65, calories: 320, distance: 5.8 },
    { day: 'Tue', steps: 7896, intensity: 'low', activeMinutes: 45, calories: 280, distance: 4.7 },
    { day: 'Wed', steps: 10254, intensity: 'high', activeMinutes: 85, calories: 410, distance: 6.9 },
    { day: 'Thu', steps: 6543, intensity: 'low', activeMinutes: 38, calories: 230, distance: 3.9 },
    { day: 'Fri', steps: 8756, intensity: 'moderate', activeMinutes: 58, calories: 340, distance: 5.2 },
    { day: 'Sat', steps: 5467, intensity: 'rest', activeMinutes: 30, calories: 190, distance: 3.2 },
    { day: 'Sun', steps: 0, intensity: 'inactive', activeMinutes: 0, calories: 0, distance: 0 }
  ];
  
  // Find maximum value for scaling
  const getMaxValue = (key) => {
    return Math.max(...chartData.map(item => item[key] || 0)) * 1.1; // Add 10% padding
  };
  
  const maxSteps = getMaxValue('steps');
  const maxCalories = getMaxValue('calories');
  const maxActiveMinutes = getMaxValue('activeMinutes');
  const maxDistance = getMaxValue('distance');
  
  // Calculate bar height percentage
  const calculateHeight = (value, max) => {
    return value ? `${(value / max) * 100}%` : '0%';
  };
  
  // Get active data based on selected tab
  const getActiveData = (day) => {
    const dayData = chartData.find(d => d.day === day) || {};
    switch (activeTab) {
      case 'steps':
        return {
          value: (dayData.steps || 0).toLocaleString(),
          unit: 'steps'
        };
      case 'calories':
        return {
          value: dayData.calories || 0,
          unit: 'kcal'
        };
      case 'time':
        return {
          value: dayData.activeMinutes || 0,
          unit: 'min'
        };
      case 'distance':
        return {
          value: dayData.distance || 0,
          unit: 'km'
        };
      default:
        return {
          value: (dayData.steps || 0).toLocaleString(),
          unit: 'steps'
        };
    }
  };
  
  // Get max value based on active tab
  const getActiveMax = () => {
    switch (activeTab) {
      case 'steps': return maxSteps;
      case 'calories': return maxCalories;
      case 'time': return maxActiveMinutes;
      case 'distance': return maxDistance;
      default: return maxSteps;
    }
  };

  // Get intensity class with fallback
  const getIntensityColor = (intensity) => {
    return intensityColors[intensity] || "bg-gray-100 text-gray-800";
  };

  // Get intensity icon with fallback
  const getIntensityIcon = (intensity) => {
    return intensityIcons[intensity] || <Activity size={14} className="text-gray-800" />;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-4 pb-2 border-b border-gray-100">
        <h3 className="text-lg font-medium">Weekly Activity</h3>
      </div>
      <div className="p-4">
        {/* Custom Tabs */}
        <div className="mb-4">
          <div className="flex rounded-md bg-gray-100 p-1">
            {['steps', 'distance', 'calories', 'time'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 rounded-sm py-1.5 text-xs font-medium transition-colors
                  ${activeTab === tab 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'}`}
              >
                {tab === 'steps' && 'Steps'}
                {tab === 'distance' && 'Distance'}
                {tab === 'calories' && 'Calories'}
                {tab === 'time' && 'Active Time'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Chart */}
        <div className="relative h-60">
          <div className="absolute left-0 top-0 h-full w-full flex justify-between items-end">
            {chartData.map((item, index) => {
              const activeData = getActiveData(item.day);
              const activeMax = getActiveMax();
              const heightValue = 
                activeTab === 'steps' ? calculateHeight(item.steps, maxSteps) :
                activeTab === 'calories' ? calculateHeight(item.calories, maxCalories) :
                activeTab === 'time' ? calculateHeight(item.activeMinutes, maxActiveMinutes) :
                calculateHeight(item.distance, maxDistance);
              
              return (
                <div 
                  key={item.day} 
                  className="flex flex-col items-center w-full relative"
                  onMouseEnter={() => setShowTooltip(index)}
                  onMouseLeave={() => setShowTooltip(null)}
                >
                  <div 
                    className={`w-8 rounded-t-sm ${item.intensity === 'inactive' ? 'bg-red-200' : 'bg-blue-600'}`} 
                    style={{ height: heightValue }}
                  ></div>
                  <div className="mt-2 text-xs font-medium">{item.day}</div>
                  
                  {/* Custom Tooltip */}
                  {showTooltip === index && (
                    <div className="absolute bottom-full mb-2 bg-white rounded-md shadow-lg p-2 z-10 min-w-32">
                      <p className="font-medium">{item.day}</p>
                      <p className="text-sm">{activeData.value} {activeData.unit}</p>
                      <div className={`mt-1 flex items-center gap-1 text-xs px-2 py-1 rounded-full ${getIntensityColor(item.intensity)}`}>
                        {getIntensityIcon(item.intensity)}
                        {capitalizeFirstLetter(item.intensity)} intensity
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* View Details Toggle */}
        <div className="mt-4 flex justify-center">
          <button 
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs flex items-center text-gray-600 hover:text-gray-900 py-1 px-2 rounded"
          >
            {showDetails ? (
              <>
                Hide details <ChevronUp className="ml-1 h-4 w-4" />
              </>
            ) : (
              <>
                View details <ChevronDown className="ml-1 h-4 w-4" />
              </>
            )}
          </button>
        </div>
        
        {/* Detailed Activity Analysis */}
        {showDetails && (
          <div className="mt-4 space-y-4">
            <h4 className="text-sm font-medium">Activity Analysis</h4>
            
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Weekly Average</p>
                  <div className="flex items-center mt-1 gap-1">
                    <Activity className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">
                      {Math.round(chartData.reduce((sum, day) => sum + (day.steps || 0), 0) / chartData.length).toLocaleString()} steps
                    </span>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500">Active Days</p>
                  <div className="flex items-center mt-1 gap-1">
                    <Calendar className="h-4 w-4 text-purple-600" />
                    <span className="font-medium">
                      {chartData.filter(day => (day.steps || 0) > 0).length}/{chartData.length} days
                    </span>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500">Total Weekly Distance</p>
                  <div className="flex items-center mt-1 gap-1">
                    <Activity className="h-4 w-4 text-purple-600" />
                    <span className="font-medium">
                      {chartData.reduce((sum, day) => sum + (day.distance || 0), 0).toFixed(1)} km
                    </span>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500">Total Calories Burned</p>
                  <div className="flex items-center mt-1 gap-1">
                    <Flame className="h-4 w-4 text-orange-500" />
                    <span className="font-medium">
                      {chartData.reduce((sum, day) => sum + (day.calories || 0), 0)} kcal
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Intensity Breakdown */}
            <div>
              <h4 className="text-sm font-medium mb-2">Activity Intensity Breakdown</h4>
              <div className="flex flex-wrap gap-2">
                {Object.keys(intensityColors).map(intensity => (
                  <div 
                    key={intensity}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${intensityColors[intensity]}`}
                  >
                    {intensityIcons[intensity]}
                    {capitalizeFirstLetter(intensity)}: 
                    {chartData.filter(day => day.intensity === intensity).length} days
                  </div>
                ))}
              </div>
            </div>
            
            {/* Movement Patterns */}
            <div>
              <h4 className="text-sm font-medium mb-2">Movement Patterns</h4>
              <div className="text-xs text-gray-600">
                <p>
                  Your most active day is <span className="font-medium">Wednesday</span> with a high intensity of activity.
                  Consider maintaining this level throughout the week for better results.
                </p>
                <p className="mt-2">
                  There's one inactive day in your week. Try to maintain at least light activity every day for best health outcomes.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityChart;